import api from "../api/api";
import { Callback, Pergunta, Resposta } from "../types/general";
import usePerguntaStore from "../zustand/use-pergunta-store";

function usePerguntaModel() {

  const {
    perguntas,
    setPerguntas,
    setSelectedPergunta,
    resetState,
    removePergunta,
    updatePergunta,
  } = usePerguntaStore();

  // Fetch all perguntas with pagination
  const fetchPerguntas = async (page: number = 1, temaId?: number, nivel?: number) => {
    try {

      const response = await api.get({
        path: "/api/perguntas",
        params: { page, tema: temaId, nivel },
      }, (data: any) => {
        setPerguntas(data);
      }, (error: any) => {
        console.error("Error fetching perguntas:", error);
      });

      return response;
    } catch (error) {
      console.error("Error fetching perguntas:", error);
      throw error;
    }
  };

  // Fetch perguntas by tema
  const fetchPerguntasByTema = async (temaId: number, page: number = 1, nivel?: number) => {
    try {
      const response = await api.get({
        path: "/api/perguntas/tema",
        params: { tema: temaId, page, nivel },
      }, (data: any) => {
        setPerguntas(data);
      }, (error: any) => {
        console.error("Error fetching perguntas by tema:", error);
      });

      return response;
    } catch (error) {
      console.error("Error fetching perguntas by tema:", error);
      throw error;
    }
  };

  // Fetch suggested perguntas
  const fetchSuggestions = async (page: number = 1, nivel?: number) => {
    try {
      const response = await api.get({
        path: "/api/perguntas/suggestions",
        params: { page, nivel },
      }, (data: any) => {
        setPerguntas(data);
      }, (error: any) => {
        console.error("Error fetching suggestions:", error);
      });

      return response;
    } catch (error) {
      console.error("Error fetching suggestions:", error);
      throw error;
    }
  };

  // Create new pergunta
  const createPergunta = async (
    data: {
      questao: string;
      respostas: Resposta[];
      nivel: number;
      temaId: number;
      author?: string;
    },
    callback?: Callback
  ) => {
    try {
      api.post({
        path: "/api/perguntas",
        params: { tema: data.temaId },
        data: {
          questao: data.questao,
          respostas: data.respostas,
          nivel: data.nivel,
          author: data.author,
        },
      }, (responseData: any) => {
        callback?.response?.(responseData);
        setPerguntas({
          ...perguntas,
          data: [...[responseData], ...perguntas.data]
        });
      }, (error: any) => {
        callback?.error?.(error);
      });
    } catch (error) {
      console.error("Error creating pergunta:", error);
      throw error;
    }
  };

  // Suggest new pergunta (visible = false)
  const suggestPergunta = async (
    data: {
      questao: string;
      respostas: Resposta[];
      nivel: number;
      temaId: number;
      author?: string;
    },
    callback?: Callback
  ) => {
    try {
      api.post({
        path: "/api/perguntas/suggest",
        params: { tema: data.temaId },
        data: {
          questao: data.questao,
          respostas: data.respostas,
          nivel: data.nivel,
          author: data.author,
        },
      }, (responseData: any) => {
        callback?.response?.(responseData);
      }, (error: any) => {
        callback?.error?.(error);
      });
    } catch (error) {
      console.error("Error suggesting pergunta:", error);
      throw error;
    }
  };

  // Update existing pergunta
  const updatePerguntaById = async (
    pergunta: Pergunta,
    callback?: Callback
  ) => {
    try {
      api.put({
        path: "/api/perguntas",
        params: { tema: pergunta.tema_id, pergunta: pergunta.id },
        data: {
          questao: pergunta.questao,
          respostas: pergunta.respostas,
          nivel: pergunta.nivel,
          visible: pergunta.visible,
          author: pergunta.author,
        },
      }, (data: any) => {
        callback?.response?.(data);
        updatePergunta(data);
      }, (error: any) => {
        callback?.error?.(error);
      });
    } catch (error) {
      console.error("Error updating pergunta:", error);
      throw error;
    }
  };

  // Validate/approve suggested pergunta
  const validatePergunta = async (id: number, callback?: Callback) => {
    try {
      api.put({
        path: `/api/perguntas/validate/${id}`,
      }, (data: any) => {
        callback?.response?.(data);
        updatePergunta({
          ...(perguntas.data.find((item) => item.id === id) as Pergunta),
          visible: true
        })
      }, (error: any) => {
        callback?.error?.(error);
      });
    } catch (error) {
      console.error("Error validating pergunta:", error);
      throw error;
    }
  };

  // Reject pergunta
  const rejectPergunta = async (id: number, callback?: Callback) => {
    try {
      api.put({
        path: `/api/perguntas/reject/${id}`,
      }, (data: any) => {
        callback?.response?.(data);
        updatePergunta({
          ...(perguntas.data.find((item) => item.id === id) as Pergunta),
          visible: false
        })
      }, (error: any) => {
        callback?.error?.(error);
      });
    } catch (error) {
      console.error("Error rejecting pergunta:", error);
      throw error;
    }
  };

  // Delete pergunta
  const deletePerguntaById = async (id: number, callback: Callback) => {
    try {
      await api.delete({
        path: `/api/perguntas/${id}`,
      }, (data: any) => {
        callback.response?.(data);
        removePergunta(id);
      }, (error: any) => {
        callback.error?.(error);
      });
    } catch (error) {
      console.error("Error deleting pergunta:", error);
      throw error;
    }
  };

  return {
    fetchPerguntas,
    fetchPerguntasByTema,
    fetchSuggestions,
    createPergunta,
    suggestPergunta,
    updatePerguntaById,
    validatePergunta,
    rejectPergunta,
    deletePerguntaById,
  };
}

export default usePerguntaModel;
